// Player Face Generator
// Random faces with dots - eyes, mouth, hair, facial hair
// Used for player cards and dot ball visualization

export type DotColor = 'white' | 'black' | 'red' | 'yellow' | 'blue' | 'green' | 'skin_light' | 'skin_medium' | 'skin_tan' | 'skin_brown' | 'skin_dark' | 'brown_dark' | 'brown' | 'blonde' | 'red_hair' | 'gray';

export type EyeStyle = 'dots' | 'oval' | 'sleepy' | 'happy' | 'angry' | 'surprised';
export type MouthStyle = 'smile' | 'frown' | 'open' | 'tongue' | 'grin' | 'meh';
export type HairStyle = 'none' | 'mohawk' | 'afro' | 'spiky' | 'balding' | 'ponytail' | 'crazy';
export type FacialHairStyle = 'none' | 'goatee' | 'mustache' | 'full' | 'stubble';

export interface FaceFeatures {
  // Background color (from dot colors)
  bgColor: DotColor;
  
  // Eyes
  eyeStyle: EyeStyle;
  eyeColor: DotColor;
  eyeSize: number; // 1-3
  
  // Mouth
  mouthStyle: MouthStyle;
  mouthColor: DotColor;
  
  // Hair
  hairStyle: HairStyle;
  hairColor: DotColor;
  
  // Facial hair
  facialHairStyle: FacialHairStyle;
  facialHairColor: DotColor;
}

const DOT_COLORS: DotColor[] = ['white', 'black', 'red', 'yellow', 'blue', 'green'];
const SKIN_TONES: DotColor[] = ['skin_light', 'skin_medium', 'skin_tan', 'skin_brown', 'skin_dark', 'white', 'black'];
const EYE_STYLES: EyeStyle[] = ['dots', 'oval', 'sleepy', 'happy', 'angry', 'surprised'];
const MOUTH_STYLES: MouthStyle[] = ['smile', 'frown', 'open', 'tongue', 'grin', 'meh'];
const HAIR_STYLES: HairStyle[] = ['none', 'mohawk', 'afro', 'spiky', 'balding', 'ponytail', 'crazy'];
const FACIAL_HAIR_STYLES: FacialHairStyle[] = ['none', 'goatee', 'mustache', 'full', 'stubble'];
const HAIR_COLORS: DotColor[] = ['black', 'brown_dark', 'brown', 'blonde', 'red_hair', 'gray', 'white'];

// Color to hex mapping
export const DOT_COLOR_HEX: Record<DotColor, string> = {
  white: '#FFFFFF',
  black: '#1a1a1a',
  red: '#ef4444',
  yellow: '#f59e0b',
  blue: '#3b82f6',
  green: '#22c55e',
  // Skin tones
  skin_light: '#FDBCB4',
  skin_medium: '#D4A373',
  skin_tan: '#C08B5C',
  skin_brown: '#8B5E3C',
  skin_dark: '#5C3D2E',
  // Hair colors
  brown_dark: '#3E2723',
  brown: '#795548',
  blonde: '#FDD835',
  red_hair: '#D84315',
  gray: '#9E9E9E',
};

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomColor(): DotColor {
  return randomItem(DOT_COLORS);
}

function randomSkinTone(): DotColor {
  return randomItem(SKIN_TONES);
}

function randomHairColor(): DotColor {
  return randomItem(HAIR_COLORS);
}

export function generateRandomFace(): FaceFeatures {
  return {
    bgColor: randomSkinTone(),
    eyeStyle: randomItem(EYE_STYLES),
    eyeColor: randomColor(),
    eyeSize: 1 + Math.floor(Math.random() * 3),
    mouthStyle: randomItem(MOUTH_STYLES),
    mouthColor: randomColor(),
    hairStyle: randomItem(HAIR_STYLES),
    hairColor: randomHairColor(),
    facialHairStyle: randomItem(FACIAL_HAIR_STYLES),
    facialHairColor: randomHairColor(),
  };
}

// Mix two faces (for forging) - takes mostly random with some features from each parent
export function mixFaces(parent1: FaceFeatures, parent2: FaceFeatures): FaceFeatures {
  const mix = generateRandomFace();
  
  // 40-60% chance to inherit each feature from either parent
  const inherit = (f1: any, f2: any) => Math.random() < 0.5 ? f1 : f2;
  
  // Background color - 50/50 chance
  mix.bgColor = Math.random() < 0.5 ? parent1.bgColor : parent2.bgColor;
  
  // Eyes - 40% parent1, 40% parent2, 20% random
  const eyeRoll = Math.random();
  if (eyeRoll < 0.4) {
    mix.eyeStyle = parent1.eyeStyle;
    mix.eyeColor = parent1.eyeColor;
    mix.eyeSize = Math.random() < 0.5 ? parent1.eyeSize : parent2.eyeSize;
  } else if (eyeRoll < 0.8) {
    mix.eyeStyle = parent2.eyeStyle;
    mix.eyeColor = parent2.eyeColor;
  }
  
  // Mouth - 40% parent1, 40% parent2, 20% random
  const mouthRoll = Math.random();
  if (mouthRoll < 0.4) {
    mix.mouthStyle = parent1.mouthStyle;
    mix.mouthColor = parent1.mouthColor;
  } else if (mouthRoll < 0.8) {
    mix.mouthStyle = parent2.mouthStyle;
    mix.mouthColor = parent2.mouthColor;
  }
  
  // Hair - 30% parent1, 30% parent2, 40% random
  const hairRoll = Math.random();
  if (hairRoll < 0.3) {
    mix.hairStyle = parent1.hairStyle;
    mix.hairColor = parent1.hairColor;
  } else if (hairRoll < 0.6) {
    mix.hairStyle = parent2.hairStyle;
    mix.hairColor = parent2.hairColor;
  }
  
  // Facial hair - 30% parent1, 30% parent2, 40% random
  const fhRoll = Math.random();
  if (fhRoll < 0.3) {
    mix.facialHairStyle = parent1.facialHairStyle;
    mix.facialHairColor = parent1.facialHairColor;
  } else if (fhRoll < 0.6) {
    mix.facialHairStyle = parent2.facialHairStyle;
    mix.facialHairColor = parent2.facialHairColor;
  }
  
  return mix;
}

// Get SVG paths for rendering
export function getEyeSVG(eyeStyle: EyeStyle, size: number): string {
  const s = size * 8;
  switch (eyeStyle) {
    case 'dots':
      return `<circle cx="${-s}" cy="0" r="${s/2}"/><circle cx="${s}" cy="0" r="${s/2}"/>`;
    case 'oval':
      return `<ellipse cx="${-s}" cy="0" rx="${s/2}" ry="${s/3}"/><ellipse cx="${s}" cy="0" rx="${s/2}" ry="${s/3}"/>`;
    case 'sleepy':
      return `<line x1="${-s-s/2}" y1="0" x2="${-s+s/2}" y2="0" stroke-width="${s/3}"/><line x1="${s-s/2}" y1="0" x2="${s+s/2}" y2="0" stroke-width="${s/3}"/>`;
    case 'happy':
      return `<path d="M${-s-s/2} ${s/4} Q${-s} ${-s/2} ${-s+s/2} ${s/4}" fill="none" stroke-width="${s/3}"/><path d="M${s-s/2} ${s/4} Q${s} ${-s/2} ${s+s/2} ${s/4}" fill="none" stroke-width="${s/3}"/>`;
    case 'angry':
      return `<path d="M${-s-s/2} ${-s/2} L${-s+s/2} ${s/4}" stroke-width="${s/4}"/><path d="M${s-s/2} ${-s/2} L${s+s/2} ${s/4}" stroke-width="${s/4}"/><circle cx="${-s}" cy="0" r="${s/3}"/><circle cx="${s}" cy="0" r="${s/3}"/>`;
    case 'surprised':
      return `<circle cx="${-s}" cy="0" r="${s/2}"/><circle cx="${s}" cy="0" r="${s/2}"/><circle cx="${-s}" cy="0" r="${s/6}"/><circle cx="${s}" cy="0" r="${s/6}"/>`;
    default:
      return `<circle cx="${-s}" cy="0" r="${s/2}"/><circle cx="${s}" cy="0" r="${s/2}"/>`;
  }
}

export function getMouthSVG(mouthStyle: MouthStyle): string {
  const s = 12;
  switch (mouthStyle) {
    case 'smile':
      return `<path d="M${-s*1.5} ${s} Q0 ${s*2} ${s*1.5} ${s}" fill="none" stroke-width="3"/>`;
    case 'frown':
      return `<path d="M${-s*1.5} ${s*1.5} Q0 ${s*0.5} ${s*1.5} ${s*1.5}" fill="none" stroke-width="3"/>`;
    case 'open':
      return `<ellipse cx="0" cy="${s}" rx="${s}" ry="${s*0.8}" fill="currentColor"/>`;
    case 'tongue':
      return `<path d="M${-s} ${s} Q0 ${s*2} ${s} ${s}" fill="none" stroke-width="3"/><ellipse cx="0" cy="${s*1.8}" rx="${s*0.5}" ry="${s*0.6}" fill="#ff6b6b"/>`;
    case 'grin':
      return `<path d="M${-s*1.5} ${s} Q0 ${s*2.5} ${s*1.5} ${s}" fill="white" stroke-width="2"/>`;
    case 'meh':
      return `<line x1="${-s}" y1="${s}" x2="${s}" y2="${s}" stroke-width="3"/>`;
    default:
      return `<path d="M${-s} ${s} Q0 ${s*1.5} ${s} ${s}" fill="none" stroke-width="3"/>`;
  }
}

export function getHairSVG(hairStyle: HairStyle): string {
  const s = 12;
  switch (hairStyle) {
    case 'none':
      return '';
    case 'mohawk':
      return `<rect x="${-s/3}" y="${-s*3}" width="${s*2/3}" height="${s*2}" fill="currentColor"/>`;
    case 'afro':
      return `<circle cx="0" cy="${-s}" r="${s*2}" fill="currentColor"/>`;
    case 'spiky':
      return `<polygon points="${-s*2},${s} ${-s},${-s*2} 0,${s} ${s},${-s*2} ${s*2},${s}" fill="currentColor"/>`;
    case 'balding':
      return `<ellipse cx="0" cy="${-s*2.5}" rx="${s*0.8}" ry="${s*0.5}" fill="currentColor"/>`;
    case 'ponytail':
      return `<circle cx="${s*1.5}" cy="${-s}" r="${s}" fill="currentColor"/><path d="M${s} ${-s} Q${s*2} ${-s*3} ${s*1.5} ${-s*2.5}" fill="currentColor"/>`;
    case 'crazy':
      return `<polygon points="${-s*2},${s/2} ${-s*1.5},${-s*3} ${-s},${s/2} 0,${-s*3} ${s},${s/2} ${s*1.5},${-s*3} ${s*2},${s/2}" fill="currentColor"/>`;
    default:
      return '';
  }
}

export function getFacialHairSVG(facialHairStyle: FacialHairStyle): string {
  const s = 10;
  switch (facialHairStyle) {
    case 'none':
      return '';
    case 'goatee':
      return `<polygon points="${-s/2},${s*1.5} 0,${s*2.5} ${s/2},${s*1.5}" fill="currentColor"/>`;
    case 'mustache':
      return `<path d="M${-s} ${s} Q${-s/2} ${s*0.5} 0 ${s} Q${s/2} ${s*0.5} ${s} ${s}" fill="currentColor"/>`;
    case 'full':
      return `<path d="M${-s} ${s/2} Q${-s} ${s*2} 0 ${s*2.5} Q${s} ${s*2} ${s} ${s/2}" fill="currentColor"/>`;
    case 'stubble':
      return `<line x1="${-s}" y1="${s*1.2}" x2="${s}" y2="${s*1.2}" stroke-width="2" stroke-dasharray="2,2"/>`;
    default:
      return '';
  }
}
